import React, { useState, useContext, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PATHS from "../../../Routes/paths";

import { ChartLegend, PopoverCard, Switcher } from "../../../Components/Atoms";
import { ModelPicker, ReactTable } from "../../../Components/Molecules";
import { Stack } from "../../../Components/Organisms/Graphs";
import { Report } from "../../../Templates";

import { getStatsData, getVehicleStats, getWidgetData } from "../../../APIs";
import { StoreContext } from "../../../Library/contexts";
import { DATE_RANGES } from "../../../Library/constants";
import { MODULE_TYPES, processReportData, STATS_ENUMS } from "../../../Library/widgets";

import { IconButton } from "@chakra-ui/react";
import { DownloadIcon } from "../../../Assets/images/svgs";
import { THRESHOLD_TABLE_HEADERS } from "../../../Library/tableHeaders";
import { getDateTimeRows, getUserAndVehicle } from "../../../Library/helpers";
import { jsonToCSVConvertor } from "../../../Library/jsonUtils";

const labelData = [ "1", "2", "3", "4", "5+" ];
const parser = { x: "model", y: "count", xLabel: "Vehicles Count", yLabel: "Times of Threshold Crossed" };
const paths = [
  { idx: 0, name: "Analytics", link: PATHS.ANALYTICS },
  { idx: 1, name: "Battery Voltage", isCurrentPage: true },
];
const NORMAL_OPTION_IDX = 3;

const BatteryVoltage = () => {
  const navigate = useNavigate();
  const { filters, constants } = useContext(StoreContext);

  const reportRef = useRef(null);
  const widgetDataRef = useRef(null);
  const reportData = useRef({});
  const tableData = useRef({});
  const [ tableHeaders, setTableHeaders ] = useState(THRESHOLD_TABLE_HEADERS);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ selected, setSelected ] = useState();
  const [ categories, setCategories ] = useState();
  const [ showOverlay, setShowOverlay ] = useState(false);

  const processData = useCallback((data, constants) => {
    // For Grouping Counts >= 5
    const processed = [];
    const legends = [];

    data.forEach((d) => {
      let counts = [];

      d.count.forEach((c) => {
        let index = (c.idx >= 5) ? 4 : c.idx - 1;

        if (counts[index]) counts[index] += c.count;
        else counts[index] = c.count;
      });

      processed.push({
        color: constants.modelMap[d.model].color,
        model: constants.modelMap[d.model].nm,
        count: counts,
        modelId: d.model,
      });

      legends.push({ label: constants.modelMap[d.model].nm, color: constants.modelMap[d.model].color });
    });

    return { graphs: processed, legends };
  }, [ ]);

  const handleSelect = useCallback(async (option, filters, constants) => {
    setIsLoading(true);
    const query = { typ: option.idx };
    if (!reportData.current[option.idx]) {
      const enums = STATS_ENUMS[MODULE_TYPES.BATTERY_VOLTAGE];
      if (enums[option.idx].graph) {
        const response = await getStatsData(DATE_RANGES[filters.range], MODULE_TYPES.BATTERY_VOLTAGE, query);
        const grouped = processReportData(response);
        reportData.current[option.idx] = processData(grouped, constants);
      }
    }

    if (!tableData.current[option.idx]) {
      const response = await getVehicleStats(DATE_RANGES[filters.range], MODULE_TYPES.BATTERY_VOLTAGE, query);
      tableData.current[option.idx] = getUserAndVehicle(response);
    }

    setSelected(option);

    if (option.idx === NORMAL_OPTION_IDX) {
      let newHeaders = {...THRESHOLD_TABLE_HEADERS};
      delete newHeaders.faults;
      setTableHeaders(newHeaders);
    }
    else setTableHeaders(THRESHOLD_TABLE_HEADERS);

    setIsLoading(false);
  }, [processData]);

  const handleFetchData = useCallback(async (filters, constants) => {
    setIsLoading(true);
    let switcherOptions = [];
    let categories = [];

    if (!widgetDataRef.current) {
      const enums = STATS_ENUMS[MODULE_TYPES.BATTERY_VOLTAGE];
      const widgetData = await getWidgetData(DATE_RANGES[filters.range], [MODULE_TYPES.BATTERY_VOLTAGE]);
      if (!widgetData || !widgetData.length || !widgetData[0].data || !widgetData[0].data.length) return;
      categories = widgetData[0].data[0] || [];
      widgetDataRef.current = categories;

      switcherOptions=[];
      Object.values(enums).forEach(async (option) => {
        let count = categories[option.idx] ? `(${categories[option.idx]})` : "";
        switcherOptions.push({
          idx: option.idx, label: `${enums[option.idx].label} ${count}`,
        });
      });

      let defaultSelected = switcherOptions[0];
      reportData.current = {};
      tableData.current = {};

      await handleSelect(defaultSelected, filters, constants);
      setCategories(switcherOptions);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (reportRef.current) reportRef.current.apply();
  }, [ handleFetchData ]);

  const getFilteredData = (data) => {
    const modelSet = new Set(filters.models);
    return data.filter((d) => modelSet.has(d.modelId));
  };

  const handleClick = (row, col) => {
    const newPaths = [ ...paths ];
    newPaths[paths.length - 1] = {
      ...newPaths[paths.length - 1],
      link: PATHS.ANALYTICS + "/" + PATHS.BATTERY_VOLTAGE,
      isCurrentPage: false,
    };
    newPaths.push({ idx: 2, name: selected.label, isCurrentPage: true });

    const graphData = getFilteredData(reportData.current[selected.idx].graphs);

    const state = {
      // model: graphData[col].modelId,
      fault: MODULE_TYPES.BATTERY_VOLTAGE,
      title: `Battery Voltage - ${selected.label}`,
      paths: newPaths,
      typ: selected ? selected.idx : NORMAL_OPTION_IDX,
      hideThresholdCrossed: selected.idx === NORMAL_OPTION_IDX,
    };

    navigate(PATHS.REPORTS, { state });
  };

  const removePopover = (toggle) => {
    if (toggle) setShowOverlay(!showOverlay);
    else setShowOverlay(false);
  };

  const columns = React.useMemo(() => Object.values(tableHeaders).map(header => {
    return {
      Header: header.nm,
      accessor: header.key,
      disableSortBy: !header.sorting,
      Cell: (row) => (header.popoverCard && row.value && row.value.length) ? (
        <PopoverCard
          data={row.value.length}
          cardTitle="Dates of Threshold crossed"
          array={getDateTimeRows(row.value)}
          removePopover={removePopover}
          dateTime={true}
        />
      ) : row.value || "-",
    };
  }), [tableHeaders]);

  const downloadData = (data) => {
    if(!data.length) return;

    var sendToExcel = data.map(a => Object.assign({}, a));
    sendToExcel.forEach((vehicle, idx) => {
      vehicle["Sr No"] = idx + 1;
      vehicle["Vehicle ID"] = vehicle.idx;
      vehicle["Device ID"] = vehicle.did;
      vehicle["Vehicle Make"] = vehicle.vmk;
      vehicle["Vehicle Model"] = vehicle.mo;
      vehicle["City"] = vehicle.user.cName;
      vehicle["State"] = vehicle.user.sName;
      vehicle["User ID"] = vehicle.uid;
      vehicle["User Name"] = `${vehicle.user.fn} ${vehicle.user.ln}`;
      vehicle["User Mob"] = vehicle.user.mob;
      vehicle["User Email"] = vehicle.user.eml;
      vehicle["Times of Threshold Crossed"] = vehicle.stat.data;
    });

    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Vehicle ID, Device ID, Vehicle Make, Vehicle Model, City, State, Times of Threshold Crossed, User ID, User Name, User Mob, User Email";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });
    if (sendData.length) jsonToCSVConvertor(sendData, "", true, "Battery_Voltage_" + Date.now(), false);
  }

  return (
    <Report breadcrumb={paths} title={"Battery Voltage"} isLoading={isLoading} onApply={handleFetchData} ref={reportRef}>
      {!isLoading && (
        <>
          <div className="panel-chart-switch">
            <Switcher options={categories} selectedOption={selected.idx} handleChange={(option) => handleSelect(option, filters, constants)} className="panel-graph-switcher panel-graph-switcher-xl" />
            <IconButton
              className="panel-button"
              aria-label='Download Report'
              icon={<DownloadIcon />}
              onClick={() => downloadData(tableData.current[selected.idx])}
            />
          </div>

          {
            reportData.current[selected.idx] && <>
              <div className="panel-chart-header">
                <ChartLegend data={reportData.current[selected.idx].legends} />
                <ModelPicker />
              </div>

              <div className="panel-chart-main">
                <label className="panel-chart-label">
                  Times of Threshold Crossed, All Vehicles
                </label>

                <Stack data={getFilteredData(reportData.current[selected.idx].graphs)} labels={labelData} parser={parser} title="Threshold Crossed" onClick={handleClick} />
              </div>
            </>
          }

          <div className="panel-chart-sheet">
            <div className="panel-chart-table">
              <ReactTable
                columns={columns}
                data={tableData.current[selected.idx]}
                showSearch={true}
                showOverlay={showOverlay}
              />
            </div>
          </div>
        </>
      )}
    </Report>
  );
}

export default BatteryVoltage;
